import _isEmpty from 'lodash/isEmpty';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../utils/empty-reference';
import { getNewDesignState } from './newDesignState';

export const BASE_CABINET_HEIGHT = 34.5;
// Standard cabinet widths: 12, 15, 18, 24, 30, 36, 42, 48
// Standard wall cabinet heights 30, 36, 42;
export const COUNTER_TOP_HEIGHT = 1.5;
export const CABINET_GAP_SPACE = 18 + COUNTER_TOP_HEIGHT; // 18 to 20
// const lowWalls = 7 * 12; // 84
// const highWalls = 9 * 12; // 108
export const STANDARD_CABINET_WIDTH = 18;
export const DEFAULT_TOUCH_POINT_WIDTH = STANDARD_CABINET_WIDTH;
export const DEFAULT_TOUCH_POINT_START_COLUMN = 6;
export const PANTRY_HEIGHT = BASE_CABINET_HEIGHT + CABINET_GAP_SPACE;
export const CABINET_GAP_TOP = BASE_CABINET_HEIGHT + COUNTER_TOP_HEIGHT;
export const CABINET_GAP_BOTTOM = CABINET_GAP_TOP + CABINET_GAP_SPACE;
export const WALL_SEGMENT_HEIGHT = 30;

export const DEFAULT_WALLSIZE = {
  Columns: 104,
  Rows: BASE_CABINET_HEIGHT + CABINET_GAP_SPACE + WALL_SEGMENT_HEIGHT // 84
};

export const DEFAULT_VIEWPORT_WALLSIZE = {
  Columns: 144,
  Rows: BASE_CABINET_HEIGHT + CABINET_GAP_SPACE + WALL_SEGMENT_HEIGHT // 84
};

export const DEFAULT_TOUCH_POINT_HEIGHT = WALL_SEGMENT_HEIGHT;
export const DEFAULT_WALL_CABINET_HEIGHT = DEFAULT_TOUCH_POINT_HEIGHT;

export function getSegments(wallWidth, wallHeight) {
  const baseTop = wallHeight - BASE_CABINET_HEIGHT;
  const baseBottom = wallHeight;
  const gapBottom = baseTop;
  const gapTop = gapBottom - CABINET_GAP_SPACE;
  const wallBottom = gapTop;
  const wallTop = wallHeight - DEFAULT_WALLSIZE.Rows;
  const constrainedWallWidth = wallWidth < DEFAULT_WALLSIZE.Columns ? DEFAULT_WALLSIZE.Columns : wallWidth;

  return [
    {
      Start: { Row: wallTop, Column: 0, Height: 0 }, // Starting position of the segment
      End: { Row: wallBottom, Column: constrainedWallWidth }, // Ending position of the segment
      Type: 'wall' // Added segment type
    },
    {
      Start: { Row: wallBottom, Column: 0, Height: 0 }, // Starting position of the segment
      End: { Row: gapBottom, Column: constrainedWallWidth },
      Type: 'gap' // Added segment type
    },
    {
      Start: { Row: gapBottom, Column: 0 }, // Starting position of another segment
      End: { Row: baseBottom, Column: constrainedWallWidth }, // Ending position of another segment
      Type: 'base' // Added segment type
    }
  ];
}

const standardHeight = 34.5;
const countertopSpace = 19.5;

const getProductsByRow = (products, rowCondition) => products
  .filter((product) => product.Origin?.Row !== undefined && product
    .Origin?.Height !== undefined && rowCondition(product.Origin.Height, product.Origin.Row));

const getProductsByHeight = (products, heightCondition) => products
  .filter((product) => product?.Origin?.Height !== undefined && heightCondition(product.Origin.Height));

const getTallestProductHeight = (products) => {
  if (!products || products.length === 0) {
    return 0; // Or null, undefined, or an appropriate default value
  }

  const tallestProduct = products
    .reduce((prev, current) => (prev.Origin.Height > current.Origin.Height ? prev : current)
    );

  return tallestProduct?.Origin.Height;
};

const calculateProductWidths = (products) => products.reduce((sum, product) => sum + (product.Origin?.Width || 0), 0);

export const selectIsStateReady = (state) => {
  const {
    designs,
    selectedDesignId,
    styleChoiceId,
    choices,
    skuDrawer: { modalAssortment }
  } = state || EMPTY_OBJECT;

  // Enhanced check: ensure the selected design exists and has valid structure
  const selectedDesignExists = selectedDesignId && designs && designs[selectedDesignId];
  const selectedDesignHasMatrix = selectedDesignExists && designs[selectedDesignId].Matrix;

  return selectedDesignId
    // && styleChoiceId
    && !_isEmpty(choices)
    && !_isEmpty(designs)
    && selectedDesignExists
    && selectedDesignHasMatrix;
  // && !_isEmpty(modalAssortment);
};

export const selectDefaultOptionChoice = (state) => {
  const choices = (state?.choices || {});
  const defaultChoiceKey = Object.keys(choices).find((key) => choices[key].isDefault);
  return choices[defaultChoiceKey];
};

export const selectOptionChoiceById = (state, choiceId) => {
  const choices = (state?.choices || {});
  return choices[choiceId];
};

export const selectCabinetStyle = (state) => {
  return selectOptionChoiceById(state, state.styleChoiceId) || selectDefaultOptionChoice(state);
};

export const selectCabinetStyleImage = (state) => {
  return selectCabinetStyle(state)?.mediaUrl;
};

export const selectCabinetStyleMetaDataItems = (state) => {
  return selectCabinetStyle(state)?.metaDataItems || EMPTY_ARRAY;
};

export const selectCabinetStyleMetaDataByName = (state, name) => {
  const items = selectCabinetStyleMetaDataItems(state);
  const lowerName = name.toLocaleLowerCase();
  return items.find((item) => item.name.toLocaleLowerCase() === lowerName);
};

export const selectCabinetStyleCollectionId = (state) => {
  return selectCabinetStyleMetaDataByName(state, 'collection id')?.value;
};

export const selectDesignById = (state, designId) => {
  return (state.designs || {})[designId] || EMPTY_OBJECT;
};

export const selectDesignProducts = (state, designId) => {
  return selectDesignById(state, designId)?.Products || EMPTY_ARRAY;
};

export const selectDesignAccessories = (state, designId) => {
  return selectDesignById(state, designId)?.Cart || EMPTY_ARRAY;
};

export const selectWallName = (state, designId) => {
  return selectDesignById(state, designId)?.DesignName;
};

export const selectDesignMatrixSize = (state, designId) => {
  const { Rows, Columns } = selectDesignById(state, designId)?.Matrix || EMPTY_OBJECT;
  return {
    Rows: parseFloat(Rows, 10),
    Columns: parseFloat(Columns, 10)
  };
};

export const selectDesignMatrixSizeWithDefault = (state, designId) => {
  const { Rows, Columns } = selectDesignById(state, designId)?.Matrix || EMPTY_OBJECT;
  return {
    Rows: Rows || DEFAULT_WALLSIZE.Rows,
    Columns: Columns || DEFAULT_WALLSIZE.Columns
  };
};
// This function is specifically for display in the EditWallModal
// It returns the actual values from the design without applying defaults
// If a user has entered dimensions, it shows those values
// If no dimensions are set, it returns empty strings
export const selectDesignMatrixSizeForDisplay = (state, designId) => {
  const matrix = selectDesignById(state, designId)?.Matrix || EMPTY_OBJECT;
  return {
    Rows: matrix.Rows || '',
    Columns: matrix.Columns || ''
  };
};
export const selectDesignSegments = (state, designId) => {
  const { Segments } = selectDesignById(state, designId)?.Matrix || EMPTY_OBJECT;
  return Segments;
};

export const selectDesignIds = (state) => {
  return state.designIds;
};

export const selectDesignName = (state, designId) => {
  const { DesignName } = selectDesignById(state, designId) || EMPTY_OBJECT;
  return DesignName;
};

const selectNewestDesign = (state) => {
  const newestDesign = Object.values(state.designs)
    .sort((itemA, itemB) => itemA.LastModified.localeCompare(itemB.LastModified))
    .pop();
  return newestDesign;
};

const selectNewestDesignHavingStyle = (state, designStyleChoiceId) => {
  const designsFiltered = Object.values(state.designs)
    .filter((design) => design.DesignStyleChoiceid === designStyleChoiceId);
  return selectNewestDesign({ designs: designsFiltered });
};

export const selectCurrentDesignOnLoad = (state, designStyleChoiceId) => {
  const isNewUser = !Object.keys(state.designs || EMPTY_OBJECT).length;
  if (designStyleChoiceId) {
    if (isNewUser) {
      return getNewDesignState(designStyleChoiceId);
    }
    return selectNewestDesignHavingStyle(state, designStyleChoiceId) || getNewDesignState();
  }
  return isNewUser ? getNewDesignState() : selectNewestDesign(state);
};

export const selectToeKickSuperSkuId = (state) => {
  return selectCabinetStyleMetaDataByName(state, 'Toe Kick SSKU ID')?.value;
};

export function selectProductSpecificationGroup(product, key) {
  const lowerKey = key.toLocaleLowerCase();
  return (product?.specificationGroup || []).find((prod) => prod.specTitle.toLocaleLowerCase() === lowerKey);
}

export function selectSpecification(group, key) {
  const lowerKey = key.toLocaleLowerCase();
  return (group?.specifications || []).find((spec) => spec.specName.toLocaleLowerCase() === lowerKey)?.specValue;
}

export function selectCabinetProductDimensions(product) {
  const dim = selectProductSpecificationGroup(product, 'dimensions');
  const depth = selectSpecification(dim, 'assembled depth (in.)') || '0 in';
  const width = selectSpecification(dim, 'assembled width (in.)') || '0 in';
  const height = selectSpecification(dim, 'assembled height (in.)') || '0 in';

  return {
    depth,
    width,
    height
  };
}

export function selectFillerProductDimensions(product) {
  const dim = selectProductSpecificationGroup(product, 'dimensions');
  const depth = selectSpecification(dim, 'product depth (in.)') || '0 in';
  const width = selectSpecification(dim, 'product width (in.)') || '0 in';
  const height = selectSpecification(dim, 'product height (in.)') || '0 in';

  return {
    depth,
    width,
    height
  };
}

export function selectProductDimensionsNumber(product) {
  const { depth, width, height } = selectCabinetProductDimensions(product);

  return {
    depth: parseFloat(depth.replace(' in', '')),
    width: parseFloat(width.replace(' in', '')),
    height: parseFloat(height.replace(' in', ''))
  };
}

export function selectProductDetails(product) {
  return selectProductSpecificationGroup(product, 'details');
}

export function selectProductColor(product) {
  return selectSpecification(selectProductDetails(product), 'color/finish');
}

export function selectSuperSkuProducts(superSkuProducts, itemIds) {
  return itemIds.reduce((acc, itemId) => {
    const matchingProducts = Object.values(superSkuProducts).filter(
      (item) => item.identifiers?.itemId === itemId
    );
    return [...acc, ...matchingProducts]; // Concatenate matching products to the accumulator
  }, []);
}

export const selectProductsForMultipleDesigns = (state, designIds) => {
  return designIds.flatMap((designId) => {
    const design = selectDesignById(state, designId);
    return design?.Products || [];
  });
};

export const selectProductListByDesignId = (state, designId) => {
  const { Products: visProducts = [], Cart: nonVisProducts = [] } = selectDesignById(state, designId);
  const allProducts = visProducts?.map((product) => (
    { itemId: product.Sku, parentId: product.ChoiceId, quantity: 1 }
  ));
  allProducts.push(...nonVisProducts?.map((product) => ({ itemId: product.Sku, quantity: product.Quantity })) || []);
  return allProducts.reduce((acc, product) => {
    const existingProduct = acc.find((pr) => pr.itemId === product.itemId);
    if (existingProduct) {
      existingProduct.quantity += product.quantity;
    } else {
      acc.push(product);
    }
    return acc;
  }, []);
};

export const selectProductsByDesignId = (state, designIds) => {
  return designIds.reduce((acc, designId) => {
    return {
      ...acc,
      [designId]: selectProductListByDesignId(state, designId)
    };
  }, {});
};

export const selectProductsModalTypeByParentId = (state, parentId) => {
  if (!state || !state.skuDrawer) {
    return null;
  }
  const { skuDrawer: { modalAssortment } } = state;
  const toeKickSuperSkuId = String(selectToeKickSuperSkuId(state));

  if (parentId) {
    if (parentId === toeKickSuperSkuId) {
      return 'Toe Kick';
    }

    const matchingEntry = Object.entries(modalAssortment)
      .find(([modalType, modalInfo]) => modalInfo.skus.find((product) => product.parentId === parentId));
    if (matchingEntry) {
      return matchingEntry[0];
    }
  }

  return 'Base';
};

export const selectProductsModalType = (state, itemId, designId) => {
  if (itemId === -1) {
    return 'Base';
  }
  if (itemId === -2) {
    return 'Wall';
  }
  const { ProductParentIds } = selectDesignById(state, designId) || EMPTY_OBJECT;
  const parentId = ProductParentIds ? ProductParentIds[`${itemId}`] : '';

  return selectProductsModalTypeByParentId(state, parentId);
};

function getFillerData(wallHeight, baseCabinetSpace, fillers) {
  const wallCabinetStartRow = wallHeight - baseCabinetSpace;

  const wallFillers = fillers.filter((filler) => {
    const fillerStartRow = filler.Origin?.Row;
    const fillerHeight = filler.Origin?.Height;
    const fillerEndRow = fillerStartRow + fillerHeight; // Calculate the end row

    // Check if the filler's end row is at or below the wall cabinet start row
    return fillerEndRow < baseCabinetSpace;
  });

  const baseFillers = fillers.filter((filler) => {
    const fillerStartRow = filler.Origin?.Row;
    const fillerHeight = filler.Origin?.Height;
    const fillerEndRow = fillerStartRow + fillerHeight;

    // Check if the filler's start row is above the wall cabinet start row
    // OR the filler's end row is above the wall cabinet start row
    return fillerEndRow === wallHeight;

  });

  const wallOverlappingBaseFillers = baseFillers.filter((filler) => {
    return filler.Origin?.Height > baseCabinetSpace;
  });

  return {
    wallFillers,
    baseFillers,
    wallOverlappingBaseFillers
  };
}

export const selectTopAndBottomProducts = (state, designId) => {
  const design = selectDesignById(state, designId);
  if (!design || _isEmpty(design.Products)) {
    return { combinedTopProducts: EMPTY_ARRAY, combinedBottomProducts: EMPTY_ARRAY };
  }
  const Wallsize = selectDesignMatrixSizeWithDefault(state, designId);
  const baseCabinetSpace = BASE_CABINET_HEIGHT + CABINET_GAP_SPACE;
  const topProducts = design.Products.filter((sku) => {
    const productType = sku.productType || selectProductsModalType(state, sku?.Sku, designId);
    return productType === 'Wall'
    || sku.productType === 'Wall'
    || sku.productType === 'WallFiller'
    || sku.productType === 'WallSpacer';
  });
  const pantryProducts = design.Products.filter((product) => {
    const productType = selectProductsModalType(state, product.Sku, designId);
    return (
      (product.Origin?.Row < 0)
      || (product.Origin?.Height > standardHeight + countertopSpace)
    ) && productType === 'Base' && product.Sku !== -2 && product.Sku !== -1;
  });

  const tallBaseSpacer = design.Products.filter((product) => {
    const productType = selectProductsModalType(state, product.Sku, designId);
    return (
      (product.Origin?.Row < 0)
      || (product.Origin?.Height > standardHeight + countertopSpace)
    ) && (productType === 'Base' || product.productType === 'BaseSpacer') && product.Sku === -1;
  });

  const fillers = design.Products.filter((sku) => {
    const productType = selectProductsModalType(state, sku?.Sku, designId);
    return (
      productType === 'Filler'
    );
  });

  const tallFillers = design.Products.filter((product) => {
    const productType = selectProductsModalType(state, product.Sku, designId);
    return (
      (product.Origin?.Row < 0)
      || (product.Origin?.Height > standardHeight + countertopSpace)
    ) && (productType === 'Filler' || product.productType === 'BaseFiller');
  });

  // const data = getFillerData(Wallsize.Rows, baseCabinetSpace, fillers);

  const bottomProducts = design.Products.filter((product) => {
    const productType = product.productType || selectProductsModalType(state, product?.Sku, designId);
    return (
      productType === 'Base' || product.productType === 'BaseFiller' || product.productType === 'BaseSpacer'
    );
  });

  const combinedTopProducts = [...topProducts, ...pantryProducts,
    ...tallBaseSpacer, ...tallFillers];
    // ...data.wallOverlappingBaseFillers, ...data.wallFillers];
  const combinedBottomProducts = [...bottomProducts];
  // ...data.baseFillers];

  return {
    combinedTopProducts,
    combinedBottomProducts
  };
};

export const selectTotalWidth = (state, designId) => {
  const { combinedTopProducts, combinedBottomProducts } = selectTopAndBottomProducts(state, designId);
  const topWidth = calculateProductWidths(combinedTopProducts);
  const bottomWidth = calculateProductWidths(combinedBottomProducts);

  return Math.max(topWidth, bottomWidth);
};

export const selectTotalHeight = (state, designId) => {
  const design = selectDesignById(state, designId);
  if (!design || _isEmpty(design.Products)) {
    return 0;
  }

  const Wallsize = selectDesignMatrixSizeWithDefault(state, designId);
  const baseCabinetSpace = BASE_CABINET_HEIGHT + CABINET_GAP_SPACE;
  const fillers = design.Products.filter((sku) => {
    const productType = selectProductsModalType(state, sku?.Sku, designId);
    return (
      productType === 'Filler'
    );
  });
  const data = getFillerData(Wallsize.Rows, baseCabinetSpace, fillers);

  const topProducts = design.Products.filter((sku) => {
    const productType = selectProductsModalType(state, sku?.Sku, designId);
    return productType === 'Wall';
  });

  const pantryProducts = design.Products.filter((product) => {
    const productType = selectProductsModalType(state, product.Sku, designId);
    return (
      (product.Origin?.Row < 0)
      || (product.Origin?.Height > standardHeight + countertopSpace)
    ) && productType === 'Base' && product.Sku !== -2 && product.Sku !== -1;
  });

  const tallestTopProduct = getTallestProductHeight(topProducts);
  const tallestPantryProduct = getTallestProductHeight(pantryProducts);

  // Calculate adjusted wall filler heights
  const adjustedWallFillerHeights = data.wallFillers.map((filler) => filler.Origin?.Height + baseCabinetSpace);

  // Get tallest height from each filler category
  const tallestWallFillerHeight = Math.max(...adjustedWallFillerHeights, 0); // Default to 0 if empty
  const tallestBaseFillerHeight = getTallestProductHeight(data.baseFillers);
  const tallestWallOverlappingBaseFillerHeight = getTallestProductHeight(data.wallOverlappingBaseFillers);

  // Get the tallest filler overall
  const tallestFiller = Math.max(
    tallestWallFillerHeight,
    tallestBaseFillerHeight,
    tallestWallOverlappingBaseFillerHeight
  );

  const topSectionHeight = standardHeight + countertopSpace + tallestTopProduct;

  return Math.max(tallestPantryProduct, topSectionHeight, tallestFiller);
};

export const selectCurrentStyleChoiceId = (state) => {
  return state.styleChoiceId;
};

export const selectTotalDepth = (state, designId) => {
  const design = selectDesignById(state, designId);
  if (!design || _isEmpty(design.Products)) {
    return 0;
  }

  const { skuDrawer: { superSkuProducts } } = state;
  const { ProductParentIds } = selectDesignById(state, designId) || EMPTY_OBJECT;
  const parentIdMap = ProductParentIds || {};

  const itemIds = design.Products.map((product) => {
    return product.Sku;
  });

  const parentIds = itemIds.map((itemId) => parentIdMap[`${itemId}`] || '');

  const filteredSuperSkuProducts = superSkuProducts.filter((superSku) => {
    return parentIds.includes(String(superSku.parentId)); // Convert to string
  });

  const assembledDepths = filteredSuperSkuProducts.map((sku) => {
    const depthValue = sku.keyProductFeatures.find(
      (feature) => feature.name === 'Assembled Depth (in.)'
    )?.value;
    return depthValue ? parseFloat(depthValue.replace(' in', '')) : 0;
  });

  // Handle empty assembledDepths to avoid Infinity
  const maxDepth = assembledDepths.length > 0 ? Math.max(...assembledDepths) : 0;

  return maxDepth;

};

export const areAllitemPricesPresent = (consolidatedProducts, itemPrices) => {
  if (!consolidatedProducts || !itemPrices || !Array.isArray(consolidatedProducts) || !Array.isArray(itemPrices)) {
    return false; // Handle null or non-array inputs
  }

  if ((consolidatedProducts.length === 0) || (itemPrices.length === 0)) {
    return false; // If consolidatedProducts is empty, all (none) are present
  }
  const itemIdsSet = new Set(itemPrices.map((item) => item.itemId));

  return consolidatedProducts.every((product) => itemIdsSet.has(product.itemId));
};

export const selectLastColorAdded = (state, designId) => {
  return selectDesignById(state, designId)?.LastColorAdded;
};

export const selectDesignAnchorSide = (state, designId) => {
  const design = selectDesignById(state, designId);
  if (!design.Products?.length) {
    return '';
  }

  return design.AnchorSide?.toLocaleLowerCase();
};

/**
 * Selects the Cabinet Visualizer Templates option from the options array/object
 * @param {Object} state - The cabinetVisualizer state
 * @returns {Object|null} The templates option or null if not found
 */
export const selectTemplateOption = (state) => {
  const { options } = state || {};
  if (!options) return null;

  // Handle both array and object structures
  const optionsArray = Array.isArray(options) ? options : Object.values(options);
  return optionsArray.find((option) => option?.internalName === 'Cabinet_Visualizer_Templates');
};

/**
 * Selects the template choices from the Cabinet Visualizer Templates option
 * @param {Object} state - The cabinetVisualizer state
 * @returns {Array} Array of template choice objects with full data
 */
export const selectTemplateChoices = (state) => {
  const templateOption = selectTemplateOption(state);
  const choiceIds = templateOption?.choices || EMPTY_ARRAY;

  if (!choiceIds.length || !state?.choices) return EMPTY_ARRAY;

  // Map choice IDs to full choice objects from state.choices
  return choiceIds
    .map((choiceId) => state.choices[choiceId])
    .filter(Boolean); // Remove any undefined/null entries
};

export const selectVizImageRoute = (state, itemId) => {
  const { styleChoiceId, choices } = state;

  if (!styleChoiceId || !choices || _isEmpty(choices) || !choices[styleChoiceId]) {
    return '';
  }
  const choiceMetaDatas = choices[styleChoiceId]?.metaDataItems;
  const visFolder = choiceMetaDatas.find((meta) => meta.name === 'Vis Folder Override')?.value ?? styleChoiceId;

  return `https://blinds.homedepot.com/is/image/cabinets/${visFolder}/${itemId}.png`;
};
